import { MAX_IMAGE_SIZE_BYTES } from '@shared/constants/mood';
import type { IJsonObject } from '../../../globalTypes';
import { sendMessageToNative } from './sendMessageToNative';

const isNative = (): boolean => typeof window !== 'undefined' && !!window.ReactNativeWebView;

interface ISaveDraftImagePayload extends IJsonObject {
  requestId: string;
  ownerId: string;
  moodDate: string;
  base64: string;
}

interface ISaveDraftImageResponse {
  requestId: string;
  success: boolean;
  imagePath?: string;
  error?: string;
}

export const saveDraftImage = async (
  ownerId: string,
  moodDate: string,
  base64: string
): Promise<string | undefined> => {
  if (!isNative()) {
    return saveDraftImageToLocalStorage(ownerId, moodDate, base64);
  }
  const requestId = `saveimg_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const payload: ISaveDraftImagePayload = {
    requestId,
    ownerId,
    moodDate,
    base64,
    targetMaxSizeBytes: MAX_IMAGE_SIZE_BYTES,
  };
  sendMessageToNative('DRAFT_IMAGE_SAVE', payload);
  return new Promise((resolve) => {
    const listener = (event: MessageEvent) => {
      try {
        const data = JSON.parse(String(event.data));
        if (data?.type === 'DRAFT_IMAGE_SAVE_RESPONSE') {
          const payload = data.payload as ISaveDraftImageResponse;
          if (payload.requestId === requestId) {
            window.removeEventListener('message', listener);
            resolve(payload.success ? payload.imagePath : undefined);
          }
        }
      } catch {
        // Ignore parse errors.
      }
    };
    window.addEventListener('message', listener);
  });
};

export const deleteDraftImage = async (ownerId: string, moodDate: string): Promise<void> => {
  if (!isNative()) {
    deleteDraftImageFromLocalStorage(ownerId, moodDate);
    return;
  }
  sendMessageToNative('DRAFT_IMAGE_DELETE', { ownerId, moodDate });
};

/* Backup for local storage in case native is not available. */
function saveDraftImageToLocalStorage(
  ownerId: string,
  moodDate: string,
  base64: string
): string | undefined {
  try {
    const key = `draft_image_${ownerId}_${moodDate}`;
    localStorage.setItem(key, base64);
    return key;
  } catch (error) {
    console.error('Error saving draft image to localStorage:', error);
    return undefined;
  }
}

/* Backup for local storage in case native is not available. */
function deleteDraftImageFromLocalStorage(ownerId: string, moodDate: string): void {
  try {
    localStorage.removeItem(`draft_image_${ownerId}_${moodDate}`);
  } catch (error) {
    console.error('Error deleting draft image from localStorage:', error);
  }
}
